import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { extractAuthFromResponse, loginWithEmail } from '../api/auth';
import { AUTH_STORAGE_KEY } from '../config/adminConfig';

const AuthContext = createContext(null);

function readStoredAuth() {
  try {
    const stored = sessionStorage.getItem(AUTH_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function getLoginErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data?.message === 'string') {
    return data.message;
  }
  if (typeof data?.error === 'string') {
    return data.error;
  }
  if (data?.errors && typeof data.errors === 'object') {
    const firstField = Object.values(data.errors)[0];
    if (Array.isArray(firstField) && firstField[0]) {
      return firstField[0];
    }
  }
  if (error?.response?.status === 401 || error?.response?.status === 422) {
    return 'Invalid email or password.';
  }
  return 'Could not sign in. Check your connection and try again.';
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => readStoredAuth());

  const login = useCallback(async (email, password) => {
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const data = await loginWithEmail(normalizedEmail, password);
      const { token, email: resolvedEmail, user } = extractAuthFromResponse(
        data,
        normalizedEmail
      );

      if (!token) {
        return { success: false, error: 'Login failed. No token received.' };
      }

      const session = {
        email: resolvedEmail,
        token,
        user,
        loggedInAt: Date.now(),
      };
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
      setAdmin(session);
      return { success: true };
    } catch (error) {
      return { success: false, error: getLoginErrorMessage(error) };
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({
      admin,
      isAuthenticated: Boolean(admin?.token),
      login,
      logout,
    }),
    [admin, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
